import { Injectable, signal, computed } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, map, tap, catchError } from 'rxjs/operators';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';

import { BusinessPartnerService } from '../../../../business-partners/business-partner/business-partner.service';
import { BusinessPartnerAddressService } from '../../../../business-partners/business-partner/business-partner-address-modal/business-partner-address.component.service';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';

import {
  TerritoryRow,
  AddressRowView,
  AssignTerritoryModalResult,
} from './interfaces/assign-territory-modal.interface';

/**
 * Service to manage shared state for AssignTerritoryModal
 * Provided at the component level so each modal instance has its own state
 */
@Injectable()
export class AssignTerritoryModalService {
  private destroy$ = new Subject<void>();

  // === STATE SIGNALS ===
  businessPartner = signal<BusinessPartnerAttributes | null>(null);
  isLoading = signal(false);
  isSaving = signal(false);

  // === SELECTION STATE (Map as Signal - single source of truth) ===
  private selectedTerritoriesMap = signal(new Map<string, TerritoryRow>());
  private selectedAddressesMap = signal(new Map<string, AddressRowView>());

  // === COMPUTED SIGNALS ===

  /**
   * Companies associated with the business partner
   */
  clientCompanies = computed(() => this.businessPartner()?.companies || []);

  /**
   * Active addresses from the business partner
   */
  clientAddresses = computed(() => {
    const bp = this.businessPartner();
    if (!bp?.addresses) return [];
    return bp.addresses.filter(addr => addr.statusId === StatusEnum.ACTIVE);
  });

  /**
   * Set of territory IDs already assigned to any client address
   */
  assignedTerritoryIds = computed(() => {
    const addresses = this.clientAddresses();
    const ids = new Set<string>();
    addresses.forEach(addr => {
      addr.routes?.forEach(route => {
        if (route._id) {
          ids.add(route._id.toString());
        }
      });
    });
    return ids;
  });

  /**
   * Display name for the client (code - name or just name)
   */
  clientDisplayName = computed(() => {
    const bp = this.businessPartner();
    if (!bp) return '';
    const code = bp.externalKeys?.code || '';
    const name = bp.name || '';
    return code ? `${code} - ${name}` : name;
  });

  /**
   * Array of selected territories
   */
  selectedTerritories = computed(() =>
    Array.from(this.selectedTerritoriesMap().values())
  );

  /**
   * Count of selected territories
   */
  selectedTerritoriesCount = computed(() =>
    this.selectedTerritoriesMap().size
  );

  /**
   * Array of selected addresses
   */
  selectedAddresses = computed(() =>
    Array.from(this.selectedAddressesMap().values())
  );

  /**
   * Count of selected addresses
   */
  selectedAddressesCount = computed(() =>
    this.selectedAddressesMap().size
  );

  /**
   * Whether user can proceed to address step
   */
  canProceedToAddress = computed(() => this.selectedTerritoriesCount() > 0);

  /**
   * Whether user can save (has territories and addresses selected)
   */
  canSave = computed(() =>
    this.selectedTerritoriesCount() > 0 && this.selectedAddressesCount() > 0
  );

  /**
   * Reactive function to check if territory is selected - O(1) lookup
   */
  readonly isTerritorySelectedFn = computed(() => {
    const map = this.selectedTerritoriesMap();
    return (id: string) => map.has(id);
  });

  /**
   * Reactive function to check if address is selected - O(1) lookup
   */
  readonly isAddressSelectedFn = computed(() => {
    const map = this.selectedAddressesMap();
    return (id: string) => map.has(id);
  });

  constructor(
    private readonly businessPartnerService: BusinessPartnerService,
    private readonly addressService: BusinessPartnerAddressService,
    private readonly handlerMessage: HandlerMessageService
  ) {}

  // === TERRITORY SELECTION METHODS ===

  /**
   * Toggle territory selection
   */
  toggleTerritory(row: TerritoryRow): void {
    this.selectedTerritoriesMap.update(map => {
      const newMap = new Map(map);
      if (newMap.has(row._id)) {
        newMap.delete(row._id);
      } else {
        newMap.set(row._id, row);
      }
      return newMap;
    });
  }

  /**
   * Select all territories from a list
   */
  selectAllTerritories(rows: TerritoryRow[]): void {
    this.selectedTerritoriesMap.update(map => {
      const newMap = new Map(map);
      rows.forEach(row => newMap.set(row._id, row));
      return newMap;
    });
  }

  /**
   * Deselect all territories from a list
   */
  deselectAllTerritories(rows: TerritoryRow[]): void {
    this.selectedTerritoriesMap.update(map => {
      const newMap = new Map(map);
      rows.forEach(row => newMap.delete(row._id));
      return newMap;
    });
  }

  /**
   * Remove a single territory from selection
   */
  removeTerritory(territory: TerritoryRow): void {
    this.selectedTerritoriesMap.update(map => {
      const newMap = new Map(map);
      newMap.delete(territory._id);
      return newMap;
    });
  }

  // === ADDRESS SELECTION METHODS ===

  /**
   * Toggle address selection
   */
  toggleAddress(row: AddressRowView): void {
    this.selectedAddressesMap.update(map => {
      const newMap = new Map(map);
      if (newMap.has(row._id)) {
        newMap.delete(row._id);
      } else {
        newMap.set(row._id, row);
      }
      return newMap;
    });
  }

  /**
   * Select all addresses from a list
   */
  selectAllAddresses(rows: AddressRowView[]): void {
    this.selectedAddressesMap.update(map => {
      const newMap = new Map(map);
      rows.forEach(row => newMap.set(row._id, row));
      return newMap;
    });
  }

  /**
   * Deselect all addresses from a list
   */
  deselectAllAddresses(rows: AddressRowView[]): void {
    this.selectedAddressesMap.update(map => {
      const newMap = new Map(map);
      rows.forEach(row => newMap.delete(row._id));
      return newMap;
    });
  }

  /**
   * Remove a single address from selection
   */
  removeAddress(address: AddressRowView): void {
    this.selectedAddressesMap.update(map => {
      const newMap = new Map(map);
      newMap.delete(address._id);
      return newMap;
    });
  }

  // === DATA LOADING ===

  /**
   * Load business partner data by ID
   */
  loadBusinessPartner(clientId: string): void {
    this.isLoading.set(true);

    this.businessPartnerService
      .findById(clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.businessPartner.set(res.data);
          this.isLoading.set(false);
        },
        error: err => {
          this.handlerMessage.error({
            message: err.error?.message,
            alternativeMessage: 'Error al cargar datos del cliente',
            statusCode: err.status,
          });
          this.isLoading.set(false);
        },
      });
  }

  // === SAVE OPERATION ===

  /**
   * Perform save operation - assigns selected territories to selected addresses
   * @returns Observable with the result
   */
  performSave(): Observable<AssignTerritoryModalResult> {
    const bpId = this.businessPartner()?._id?.toString();
    if (!bpId) return of({ success: false });

    this.isSaving.set(true);

    const routeIds = this.selectedTerritories().map(t => ({
      id: t._id,
      isPrincipal: false,
    }));

    const selectedAddresses = this.selectedAddresses();
    const allAddresses = this.clientAddresses();

    if (selectedAddresses.length === 0) {
      this.isSaving.set(false);
      return of({ success: false });
    }

    // Prepare updates for each selected address
    const updates = selectedAddresses.map(selectedAddr => {
      const fullAddress = allAddresses.find(
        a => a._id?.toString() === selectedAddr._id
      );

      // Get existing routes from this address
      const existingRouteIds =
        fullAddress?.routes?.map(r => ({
          id: r._id.toString(),
          isPrincipal: r.isPrincipal || false,
        })) || [];

      // Merge: preserve existing + add new without duplicates
      const mergedRouteIds = [...existingRouteIds];
      routeIds.forEach(newRoute => {
        if (!mergedRouteIds.some(r => r.id === newRoute.id)) {
          mergedRouteIds.push(newRoute);
        }
      });

      return {
        addressId: selectedAddr._id,
        routeIds: mergedRouteIds,
      };
    });

    return this.addressService.updateMultipleAddressesTerritories(bpId, updates).pipe(
      map(() => ({
        success: true,
        addressIds: selectedAddresses.map(a => a._id),
        territoriesAssigned: routeIds.length,
        addressesUpdated: updates.length,
      } as AssignTerritoryModalResult)),
      tap(() => {
        this.handlerMessage.success({
          message: `Territorios asignados a ${updates.length} dirección(es) con éxito`,
        });
        this.isSaving.set(false);
      }),
      catchError(err => {
        this.handlerMessage.error({
          message: err.error?.message,
          alternativeMessage: 'Error al asignar territorios',
          statusCode: err.status,
        });
        this.isSaving.set(false);
        return of({ success: false });
      })
    );
  }

  // === CLEANUP ===

  /**
   * Reset all state
   */
  reset(): void {
    this.selectedTerritoriesMap.set(new Map());
    this.selectedAddressesMap.set(new Map());
    this.businessPartner.set(null);
    this.isLoading.set(false);
    this.isSaving.set(false);
  }

  /**
   * Cleanup on destroy
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
